import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, createCorrection, fetchCorrections, fetchMyAttendance } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { todayInZone } from '../lib/attendance-session';
import {
  allowsFutureWorkDate,
  buildRequestedPayload,
  CORRECTION_TYPES,
  formatCorrectionType,
  formatHalfDayPart,
  formatLeaveType,
  HALF_DAY_PARTS,
  LEAVE_TYPES,
  REASON_MAX_LENGTH,
  WORK_MODES,
  WORK_MODE_LABEL,
  requiresEventType,
  requiresWorkMode,
  requiresHalfDayPart,
  requiresLeaveType,
  requiresProposedTime,
  validateCorrectionForm,
  type CorrectionFormValues,
} from '../lib/correction-form';
import {
  calendarYearRange,
  countsTowardQuota,
  describeLeaveUsage,
  formatDays,
  summarizeLeave,
  type LeaveUsage,
} from '../lib/leave-quota';
import { ErrorCard } from '../states';

/**
 * Ajukan koreksi absensi.
 *
 * Validasi klien MEMANDU; server tetap yang memutuskan. Urutan pemeriksaannya sama dengan
 * `CreateCorrectionDto` supaya pesan yang dilihat pengguna sama baik ditolak lebih awal di sini
 * maupun ditolak server — dua pesan berbeda untuk satu sebab akan terlihat seperti bug.
 */
export function CorrectionNewPage(): ReactNode {
  const session = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const today = todayInZone(new Date(), timezone);

  const [values, setValues] = useState<CorrectionFormValues>({
    workDate: params.get('workDate') ?? today,
    correctionType: 'MISSING_CHECK_OUT',
    reason: '',
    proposedTime: '',
    leaveType: '',
    halfDayPart: '',
    eventType: '',
    workMode: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
   * Pemakaian cuti tahun ini — diambil MALAS, hanya saat jenisnya "Cuti / izin" atau "Izin
   * setengah hari" (keduanya memakan jatah 14 hari — lihat `summarizeLeave`).
   *
   * Dua permintaan jaringan pada setiap pembukaan layar Koreksi akan dibayar oleh semua orang,
   * padahal empat dari enam jenis koreksi tidak memerlukannya sama sekali; layar ini dibuka
   * kurir di jalan, dengan sinyal seadanya.
   *
   * `fetchCorrections` dipanggil TANPA filter status — sengaja, sejak izin setengah hari ada.
   * Persetujuannya tidak pernah tercatat di `AttendanceDay` (lihat komentar enum di server),
   * jadi baris koreksi APPROVED itu SENDIRI satu-satunya sumber "sudah disetujui berapa hari" —
   * memfilter ke PENDING saja akan membuat separuh jatah izin setengah hari tidak pernah terlihat.
   *
   * Kegagalannya SENGAJA senyap. Angka jatah adalah keterangan, bukan syarat: memerahkan layar
   * pengajuan karena penghitung tidak dapat dimuat akan menghalangi orang mengajukan cuti yang
   * berhak ia ajukan. Yang tampil hanya "Menghitung sisa cuti…" yang tidak pernah selesai — dan
   * itu jujur, karena memang begitu keadaannya.
   */
  const [cuti, setCuti] = useState<LeaveUsage | null>(null);
  const perluJatah =
    values.correctionType === 'MARK_ON_LEAVE' || values.correctionType === 'MARK_HALF_DAY_LEAVE';
  useEffect(() => {
    if (!perluJatah || cuti !== null) return;
    let aktif = true;
    const { from, to } = calendarYearRange(today);
    void Promise.all([fetchMyAttendance(session.auth, from, to), fetchCorrections(session.auth)])
      .then(([riwayat, koreksi]) => {
        if (aktif) setCuti(summarizeLeave(riwayat.days, koreksi, today));
      })
      .catch(() => {
        /* senyap — lihat alasannya di atas */
      });
    return () => {
      aktif = false;
    };
  }, [perluJatah, cuti, session.auth, today]);

  const problems = validateCorrectionForm(values, today);
  const problemFor = (field: string) =>
    submitted ? (problems.find((p) => p.field === field)?.message ?? null) : null;

  const set = <K extends keyof CorrectionFormValues>(key: K, value: CorrectionFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (problems.length > 0) {
      return;
    }
    setSending(true);
    try {
      await createCorrection(session.auth, {
        // Koreksi atas absensi sendiri. Mengajukan untuk orang lain adalah alur manajer yang
        // belum ada layarnya — server tetap menerimanya, klien ini belum menawarkannya.
        subjectUserId: session.me.userId,
        workDate: values.workDate,
        correctionType: values.correctionType,
        requestedPayload: buildRequestedPayload(values),
        reason: values.reason.trim(),
        attachmentFileId: null,
      });
      navigate('/attendance/corrections');
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <form className="card" onSubmit={submit} noValidate>
        <h2>Ajukan koreksi absensi</h2>

        {/*
          Label DAN batas atas keduanya bergantung pada jenis koreksinya. Empat tipe yang
          memperbaiki fakta yang sudah terjadi tetap terkunci ke hari ini atau sebelumnya — batas
          `max` itu bukan formalitas, "lupa check-in besok" tidak bermakna. Jenis izin sengaja
          TIDAK diberi `max`: izin lazim diajukan sebelum harinya, dan menahan itu adalah gap
          yang sudah lama diketahui sebelum diperbaiki di sini.
        */}
        <label htmlFor="workDate">
          {allowsFutureWorkDate(values.correctionType) ? 'Tanggal' : 'Tanggal yang dikoreksi'}
        </label>
        <input
          id="workDate"
          type="date"
          value={values.workDate}
          max={allowsFutureWorkDate(values.correctionType) ? undefined : today}
          onChange={(e) => set('workDate', e.target.value)}
        />
        {problemFor('workDate') ? (
          <p className="warning small" role="alert">
            {problemFor('workDate')}
          </p>
        ) : null}

        <label htmlFor="correctionType">Jenis koreksi</label>
        <select
          id="correctionType"
          value={values.correctionType}
          onChange={(e) => set('correctionType', e.target.value)}
        >
          {CORRECTION_TYPES.map((type) => (
            <option key={type} value={type}>
              {formatCorrectionType(type)}
            </option>
          ))}
        </select>

        {requiresLeaveType(values.correctionType) ? (
          <>
            <label htmlFor="leaveType">Jenis izin</label>
            <select
              id="leaveType"
              value={values.leaveType}
              onChange={(e) => set('leaveType', e.target.value)}
            >
              <option value="">Pilih jenis…</option>
              {LEAVE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {formatLeaveType(type)}
                </option>
              ))}
            </select>
            {problemFor('leaveType') ? (
              <p className="warning small" role="alert">
                {problemFor('leaveType')}
              </p>
            ) : null}
          </>
        ) : null}

        {/*
         * Dua isian yang sebelumnya TIDAK ADA — dan ketiadaannya membuat server menolak
         * kirimannya dengan galat mentah. "Lupa absen" sengaja tidak menampilkan pilihan ini:
         * jenis absennya sudah tertulis di nama koreksinya.
         */}
        {requiresEventType(values.correctionType) ? (
          <>
            <label htmlFor="eventType">Absen yang dikoreksi</label>
            <select
              id="eventType"
              value={values.eventType}
              onChange={(e) => set('eventType', e.target.value)}
            >
              <option value="">Pilih…</option>
              <option value="CHECK_IN">Absen masuk</option>
              <option value="CHECK_OUT">Absen pulang</option>
            </select>
            {problemFor('eventType') ? (
              <p className="warning small" role="alert">
                {problemFor('eventType')}
              </p>
            ) : null}
          </>
        ) : null}

        {requiresWorkMode(values.correctionType) ? (
          <>
            <label htmlFor="workMode">Mode kerja yang benar</label>
            <select
              id="workMode"
              value={values.workMode}
              onChange={(e) => set('workMode', e.target.value)}
            >
              <option value="">Pilih mode…</option>
              {WORK_MODES.map((m) => (
                <option key={m} value={m}>
                  {WORK_MODE_LABEL[m]}
                </option>
              ))}
            </select>
            {problemFor('workMode') ? (
              <p className="warning small" role="alert">
                {problemFor('workMode')}
              </p>
            ) : null}
          </>
        ) : null}

        {requiresHalfDayPart(values.correctionType) ? (
          <>
            <label htmlFor="halfDayPart">Bagian hari</label>
            <select
              id="halfDayPart"
              value={values.halfDayPart}
              onChange={(e) => set('halfDayPart', e.target.value)}
            >
              <option value="">Pilih bagian…</option>
              {HALF_DAY_PARTS.map((part) => (
                <option key={part} value={part}>
                  {formatHalfDayPart(part)}
                </option>
              ))}
            </select>
            {problemFor('halfDayPart') ? (
              <p className="warning small" role="alert">
                {problemFor('halfDayPart')}
              </p>
            ) : null}
          </>
        ) : null}

        {/*
          Jatah cuti ditampilkan hanya saat jenis yang dipilih benar-benar MEMAKAN jatah. Untuk
          "Cuti / izin": Sakit dan Cuti melahirkan tidak memakan, jadi digerbangi
          `countsTowardQuota`. Untuk "Izin setengah hari": SELALU memakan 0,5 hari — tidak ada
          varian yang dikecualikan, jadi tidak ada gerbang tambahan.
        */}
        {(requiresLeaveType(values.correctionType) && countsTowardQuota(values.leaveType)) ||
        requiresHalfDayPart(values.correctionType) ? (
          <div className={`geo-box${cuti && cuti.overLimit ? ' warn' : ''}`}>
            {cuti === null ? (
              <p className="muted small">Menghitung sisa cuti…</p>
            ) : (
              <>
                <p className="geo-title">Cuti tahun {cuti.year}</p>
                <p className="geo-accuracy">
                  {formatDays(cuti.approved)} / {cuti.limit}
                </p>
                <p className="muted small">{describeLeaveUsage(cuti)}</p>
                {cuti.overLimit ? (
                  <p className="warn-text small">
                    Sudah melewati jatah {cuti.limit} hari. Pengajuan ini tetap dapat dikirim —
                    penyelia yang memutuskan.
                  </p>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {requiresProposedTime(values.correctionType) ? (
          <>
            <label htmlFor="proposedTime">Jam usulan ({timezone})</label>
            <input
              id="proposedTime"
              type="time"
              value={values.proposedTime}
              onChange={(e) => set('proposedTime', e.target.value)}
            />
            {problemFor('proposedTime') ? (
              <p className="warning small" role="alert">
                {problemFor('proposedTime')}
              </p>
            ) : null}
          </>
        ) : null}

        <label htmlFor="reason">Alasan (wajib)</label>
        <textarea
          id="reason"
          rows={4}
          value={values.reason}
          maxLength={REASON_MAX_LENGTH}
          onChange={(e) => set('reason', e.target.value)}
          aria-describedby="reason-hint"
        />
        <p id="reason-hint" className="muted small">
          Manajer yang memutuskan hanya melihat apa yang Anda tulis di sini. Sebutkan apa yang
          terjadi — mis. &quot;baterai habis sebelum sempat absen pulang&quot;.
        </p>
        {problemFor('reason') ? (
          <p className="warning small" role="alert">
            {problemFor('reason')}
          </p>
        ) : null}

        <button type="submit" className="button-primary" disabled={sending}>
          {sending ? 'Mengirim…' : 'Kirim pengajuan'}
        </button>
      </form>

      {error ? <ErrorCard error={error} /> : null}
    </>
  );
}
